import * as React from "react";
import AddJobFormView from "./AddJobFormView";
import {OnFailure, OnSuccess, OnSuccessData} from "../../../store/action/actionHelpers";
import JobData from "../../../model/job/JobData";
import Job from "../../../model/job/Job";
import {option, Option} from "ts-option";
import Toast from "../../helper/Toast";
import {isDefined} from "../../../utils/Utils";
import {connect, Dispatch} from "react-redux";
import {createJob, updateJobAgent, updateJobWatchers} from "../../../store/action/job/job";
import Site from "../../../model/site/Site";
import SiteData from "../../../model/site/SiteData";
import {createSite} from "../../../store/action/site/site";
import MentionUtils from "../../../utils/MentionUtils";
import {api} from "../../../api/api";
import _ = require("underscore");

interface ComponentProps {
	/**
	 * On success callback.
	 */
	onSuccess?: () => void;
}

interface DispatchProps {
	/**
	 * Create job action
	 * @param job Job data to send.
	 * @param OnSuccessData On job create success callback.
	 * @param onFailure On job create failure callback.
	 */
	createJob: (job: JobData, onSuccess: OnSuccessData<Job>, onFailure: OnFailure) => void
	/**
	 * Create site action.
	 * @param {SiteData} site site data to send.
	 * @param {OnSuccessData<Site>} onSuccess on site creation success callback.
	 * @param {OnFailure} onFailure on site creation failure callback.
	 */
	createSite: (site: SiteData, onSuccess: OnSuccessData<Site>, onFailure: OnFailure) => void
	/**
	 * Update watchers action
	 * @param {string} jobId Job Id.
	 * @param {string[]} watchers Array of users Id's.
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	updateJobWatchers: (jobId: string, watchers: string[], onSuccess: OnSuccess, onFailure: OnFailure) => void
	/**
	 * Update job agent.
	 * @param jobId Job Id.
	 * @param agentId Agent Id.
	 * @param onSuccess
	 * @param onFailure
	 */
	updateJobAgent: (jobId: string, agentId: Option<string>, onSuccess: OnSuccess, onFailure: OnFailure) => void
}

interface State {
	/**
	 * Indicates if data is saving.
	 */
	isJobSaving: boolean;
}

type Props = ComponentProps & DispatchProps;

class AddJobForm extends React.Component<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {
			isJobSaving: false,
		};
		this.onSuccess = this.onSuccess.bind(this);
		this.onFailure = this.onFailure.bind(this);
		this.createJob = this.createJob.bind(this);
		this.createJobWithSite = this.createJobWithSite.bind(this);
		this.mentionUsers = this.mentionUsers.bind(this);
	}

	render() {
		return <AddJobFormView isLoading={this.state.isJobSaving} onSubmit={this.createJobWithSite} label={""}/>
	}

	/**
	 * Create job success callback.
	 */
	onSuccess() {
		this.setState({isJobSaving: false});
		Toast.success("Job saved", "Job submission");
		if (isDefined(this.props.onSuccess)) {
			this.props.onSuccess();
		}
	}

	/**
	 * Creating job failure callback.
	 */
	onFailure() {
		Toast.error("Sorry, something went wrong. Try again later.", "Create Job error");
		this.setState({isJobSaving: false});
	}

	createJob(jobData: JobData, watchers: string[], agentId: Option<string>) {
		this.props.createJob(jobData, (job) => {
				this.props.updateJobWatchers(job.id, watchers, () => {}, () => {
					this.setState({isJobSaving: false});
					Toast.error("Sorry, something went wrong. Try again later.", "Update watchers error");
				});
				this.props.updateJobAgent(job.id, agentId, () => {}, () => {
					this.setState({isJobSaving: false});
					Toast.error("Sorry, something went wrong. Try again later.", "Update Agent error");
				});
				this.mentionUsers(job);
				this.onSuccess();
			},
			this.onFailure)
	};

	createJobWithSite(jobData: JobData,
		siteData: Option<SiteData>,
		watchers: string[],
		agentId: Option<string>) {
		this.setState({isJobSaving: true}, () => {
			siteData.map(sd =>
				this.props.createSite(sd, (site) => {
						this.createJob(jobData.copy({siteId: option(site.id)}), watchers, agentId)
					},
					() => {
						this.setState({isJobSaving: false});
						Toast.error("Sorry, something went wrong. Try again later.", "Create Site error");
					}))
				.orElse(() => {
					this.createJob(jobData, watchers, agentId);
					return null;
				});
		})
	}

	private mentionUsers(job: Job) {
		job.data.extendedDescription.map(d => {
			const uniqueUsers = _.uniq(MentionUtils.getMentions(d).map(m => m.id));
			if (uniqueUsers.length)
				api.instance.jobApi.mentionUsers(job.id, uniqueUsers).catch(() => Toast.error("Mention users error"));
		})
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		createJob: (job: JobData, onSuccess: OnSuccessData<Job>, onFailure: OnFailure) => dispatch(
			createJob(job, onSuccess, onFailure)
		),
		createSite: (site: SiteData, onSuccess: OnSuccessData<Site>, onFailure: OnFailure) => dispatch(
			createSite(site, onSuccess, onFailure)
		),
		updateJobWatchers: (jobId: string, watchers: string[], onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			updateJobWatchers(jobId, watchers, onSuccess, onFailure)
		),
		updateJobAgent: (jobId: string, agentId: Option<string>, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			updateJobAgent(jobId, agentId, onSuccess, onFailure)
		)
	}
}

export default connect(null, mapDispatchToProps)(AddJobForm)